import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "../SoapForm.css";
import {
  saveSoapNote,
  updateSoapNote,
  completeSoapNote,
  approveSoapNote,
  fetchPrecedingSoapNote,
  fetchAppointmentById,
} from "../../store/thunks/appointmentsThunk";
import { XMarkIcon } from "@heroicons/react/24/outline";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const SOAPForm = ({ onClose, appointment: initialAppointment }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [appointment, setAppointment] = useState(initialAppointment);
  const [isNewSoapNote, setIsNewSoapNote] = useState(true);
  const [isFreeform, setIsFreeform] = useState(false);
  const [soapData, setSoapData] = useState([]);
  const [structuredData, setStructuredData] = useState({
    subjective: "",
    objective: "",
    assessment: "",
    plan: "",
  });
  const [isSignedOff, setIsSignedOff] = useState(false);
  const [isPopulated, setIsPopulated] = useState(false);
  const [showApprovalView, setShowApprovalView] = useState(false);

  useEffect(() => {
    if (appointment && appointment.soapNote) {
      setIsNewSoapNote(appointment.soapNote.length === 0);
      if (appointment.soapNoteCompleted && !appointment.soapNoteFinalized) {
        setShowApprovalView(true);
      }
    }
  }, [appointment]);

  useEffect(() => {
    if (appointment?.soapNote?.length > 0) {
      const latestNote = appointment.soapNote[appointment.soapNote.length - 1];
      setIsSignedOff(!!latestNote.signedOffBy);

      if (isPopulated || !isNewSoapNote) {
        if (latestNote.templateUsed === "freeform") {
          setIsFreeform(true);
          const freeformFields = Object.entries(latestNote.noteData).map(
            ([name, value]) => ({ id: Date.now() + Math.random(), name, value })
          );
          setSoapData(freeformFields);
        } else if (latestNote.templateUsed === "structured") {
          setIsFreeform(false);
          setStructuredData(latestNote.noteData || {});
        }
      }
    }
  }, [appointment, isPopulated, isNewSoapNote]);

  const handleAddField = () => {
    setSoapData((prev) => [...prev, { id: Date.now(), name: "", value: "" }]);
  };

  const handlePopulateSoap = async () => {
    if (appointment.blockId && appointment.blockNumber > 1) {
      try {
        const response = await dispatch(
          fetchPrecedingSoapNote(appointment._id)
        ).unwrap();

        if (response) {
          const { noteData, templateUsed } = response;
          if (templateUsed === "freeform") {
            setIsFreeform(true);
            const freeformFields = Object.entries(noteData).map(
              ([name, value]) => ({
                id: Date.now() + Math.random(),
                name,
                value,
              })
            );
            setSoapData(freeformFields);
          } else if (templateUsed === "structured") {
            setIsFreeform(false);
            setStructuredData(noteData);
          }
          setIsPopulated(true);
        }
      } catch (error) {
        console.error("Failed to fetch preceding SOAP note:", error);
        window.alert("No SOAP note found in the preceding appointment.");
      }
    } else {
      window.alert(
        "Cannot populate SOAP note. This appointment is the first in the block."
      );
    }
  };

  const handleSave = async () => {
    const note = isFreeform
      ? {
          noteData: soapData.reduce((acc, field) => {
            acc[field.name] = field.value;
            return acc;
          }, {}),
          createdBy: user._id,
          templateUsed: "freeform",
        }
      : {
          noteData: structuredData,
          createdBy: user._id,
          templateUsed: "structured",
        };

    try {
      if (isNewSoapNote) {
        await dispatch(saveSoapNote([appointment, user, note])).unwrap();
        window.alert("SOAP Note Saved.");
      } else {
        await dispatch(updateSoapNote([appointment, user, note])).unwrap();
        window.alert("SOAP Note Updated.");
      }
      const updatedAppointment = await dispatch(
        fetchAppointmentById(appointment._id)
      ).unwrap();
      setAppointment(updatedAppointment);
    } catch (error) {
      console.error("Save/Update failed:", error);
      window.alert(
        "An error occurred while saving/updating the SOAP Note. Please try again."
      );
    }
  };

  const handleCompleteSoapNote = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to save and complete the SOAP Note? Once completed, it cannot be edited."
    );
    if (!confirmed) return;

    const note = isFreeform
      ? {
          noteData: soapData.reduce((acc, field) => {
            acc[field.name] = field.value;
            return acc;
          }, {}),
          createdBy: user._id,
          templateUsed: "freeform",
        }
      : {
          noteData: structuredData,
          createdBy: user._id,
          templateUsed: "structured",
        };

    try {
      if (isNewSoapNote) {
        await dispatch(saveSoapNote([appointment, user, note])).unwrap();
      } else {
        await dispatch(updateSoapNote([appointment, user, note])).unwrap();
      }
      const completedResponse = await dispatch(
        completeSoapNote([appointment, user])
      ).unwrap();
      const updatedAppointment = await dispatch(
        fetchAppointmentById(appointment._id)
      ).unwrap();

      // Update the local state with the new appointment data
      setAppointment(updatedAppointment);
      setShowApprovalView(true);

      window.alert("SOAP Note Completed. You can now approve it.");
    } catch (error) {
      console.error("Error completing SOAP note:", error);
      window.alert(
        "An error occurred while completing the SOAP Note. Please try again."
      );
    }
  };

  const handleApprove = async () => {
    const confirmed = window.confirm(
      "This action cannot be undone. Once approved, the SOAP Note cannot be edited."
    );
    if (!confirmed) return;

    try {
      const finalNote = {
        noteData: isFreeform
          ? soapData.reduce((acc, field) => {
              acc[field.name] = field.value;
              return acc;
            }, {})
          : structuredData,
        templateUsed: isFreeform ? "freeform" : "structured",
      };

      await dispatch(
        approveSoapNote({
          appointment,
          user,
          finalNote,
        })
      ).unwrap();
      const updatedAppointment = await dispatch(
        fetchAppointmentById(appointment._id)
      ).unwrap();
      setAppointment(updatedAppointment);
      window.alert("SOAP Note Approved successfully.");
      onClose();
    } catch (error) {
      console.error("Approve SOAP Note failed:", error);
    }
  };

  const canApproveNote =
    appointment.soapNoteCompleted &&
    (user.specialization?.includes("SLP") ||
      user._id === appointment.client?.supervisingClinician?.current);

  const canEditNote = !appointment.soapNoteCompleted || canApproveNote;

  const renderClientInfo = () => (
    <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-sm mb-4">
      <div className="space-y-1">
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-500">Client:</span>
          <span className="ml-2 text-sm text-gray-900">
            {appointment.client.firstName} {appointment.client.lastName}
          </span>
        </div>
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-500">D.O.B:</span>
          <span className="ml-2 text-sm text-gray-900">
            {appointment.client.dateOfBirth
              ? appointment.client.dateOfBirth.split("T")[0]
              : "N/A"}
          </span>
        </div>
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-500">
            Treating Clinician:
          </span>
          <span className="ml-2 text-sm text-gray-900">
            {appointment.clinician.firstName} {appointment.clinician.lastName}
          </span>
        </div>
      </div>
      <div className="space-y-1">
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-500">
            Supervising Clinician:
          </span>
          <span className="ml-2 text-sm text-gray-900">
            {appointment.supervisingClinician
              ? `${appointment.supervisingClinician.firstName} ${appointment.supervisingClinician.lastName}`
              : "N/A"}
          </span>
        </div>
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-500">
            Session Date:
          </span>
          <span className="ml-2 text-sm text-gray-900">
            {appointment.start
              ? new Date(appointment.start).toLocaleString("en-US", {
                  year: "numeric",
                  month: "short",
                  weekday: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                })
              : "N/A"}
          </span>
        </div>
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-500">Duration:</span>
          <span className="ml-2 text-sm text-gray-900">
            {appointment.end && appointment.start
              ? `${Math.round(
                  (new Date(appointment.end) - new Date(appointment.start)) /
                    60000
                )} min`
              : "N/A"}
          </span>
        </div>
      </div>
    </div>
  );

  const renderApprovalView = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              This SOAP note is completed and ready for approval. Please review
              the content below and approve if everything is correct.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-sm shadow">
        {isFreeform ? (
          <div className="space-y-4 p-4">
            {soapData.map((field) => (
              <div key={field.id} className="border-b pb-4">
                <h4 className="font-medium text-gray-900">{field.name}</h4>
                <div
                  className="prose max-w-none mt-2"
                  dangerouslySetInnerHTML={{ __html: field.value }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4 p-4">
            {Object.entries(structuredData).map(([key, value]) => (
              <div key={key} className="border-b pb-4">
                <h4 className="font-medium text-gray-900">
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </h4>
                <div
                  className="prose max-w-none mt-2"
                  dangerouslySetInnerHTML={{ __html: value }}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-4">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm shadow-sm hover:bg-gray-50">
          Close
        </button>
        <button
          onClick={handleApprove}
          disabled={!canApproveNote}
          className={`px-4 py-2 text-sm font-medium text-white rounded-sm shadow-sm ${
            canApproveNote
              ? "bg-green-600 hover:bg-green-700"
              : "bg-gray-400 cursor-not-allowed"
          }`}>
          Approve SOAP Note
        </button>
      </div>
    </div>
  );

  const renderEditView = () => (
    <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
      <div className="flex items-center justify-between border-b border-gray-200 pb-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900">SOAP Note</h2>
          <p className="mt-1 text-sm text-gray-500">
            {isFreeform ? "Freeform" : "Structured"} format
          </p>
        </div>
        {isNewSoapNote && !appointment.soapNoteCompleted && (
          <div className="flex items-center space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                checked={!isFreeform}
                onChange={() => setIsFreeform(false)}
                className="form-radio h-4 w-4 text-indigo-600"
              />
              <span className="ml-2 text-sm text-gray-700">Structured</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                checked={isFreeform}
                onChange={() => setIsFreeform(true)}
                className="form-radio h-4 w-4 text-indigo-600"
              />
              <span className="ml-2 text-sm text-gray-700">Freeform</span>
            </label>
          </div>
        )}
      </div>

      {isFreeform ? (
        <div className="space-y-4">
          {soapData.map((field, index) => (
            <div key={field.id} className="space-y-2">
              <input
                type="text"
                placeholder={`Field Name ${index + 1}`}
                value={field.name}
                onChange={(e) =>
                  setSoapData((prev) =>
                    prev.map((item) =>
                      item.id === field.id
                        ? { ...item, name: e.target.value }
                        : item
                    )
                  )
                }
                disabled={!canEditNote}
                className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
              <ReactQuill
                value={field.value}
                onChange={(content) =>
                  setSoapData((prev) =>
                    prev.map((item) =>
                      item.id === field.id ? { ...item, value: content } : item
                    )
                  )
                }
                readOnly={!canEditNote}
                theme="snow"
              />
            </div>
          ))}
          {canEditNote && !appointment.soapNoteCompleted && (
            <button
              type="button"
              onClick={handleAddField}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
              Add Field
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(structuredData).map(([key, value]) => (
            <div key={key} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </label>
              <ReactQuill
                value={value}
                onChange={(content) =>
                  setStructuredData((prev) => ({
                    ...prev,
                    [key]: content,
                  }))
                }
                readOnly={!canEditNote}
                theme="snow"
              />
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={handlePopulateSoap}
          disabled={!appointment.blockId}
          className={`inline-flex items-center px-2.5 py-1.5 text-sm font-medium rounded-sm shadow-sm transition-colors ${
            appointment.blockId
              ? "bg-indigo-600 text-white hover:bg-indigo-700"
              : "bg-gray-100 text-gray-500 cursor-not-allowed"
          }`}>
          Populate from Previous
        </button>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={!canEditNote}
            className="inline-flex items-center px-2.5 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-sm shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            Save
          </button>
          <button
            type="button"
            onClick={handleCompleteSoapNote}
            disabled={!canEditNote}
            className="inline-flex items-center px-2.5 py-1.5 bg-green-600 text-white text-sm font-medium rounded-sm shadow-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            Complete
          </button>
        </div>
      </div>
    </form>
  );

  return (
    <div className="fixed inset-0 flex items-center justify-center z-70 overflow-y-auto">
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-sm shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="absolute top-0 right-0 pt-4 pr-4">
          <button
            onClick={onClose}
            className="rounded-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {renderClientInfo()}
          {showApprovalView ? renderApprovalView() : renderEditView()}
        </div>
      </div>
    </div>
  );
};

export default SOAPForm;
